<template>
  <VForm :validation-schema="Schema" :initial-values="Schema.getDefault()" v-slot="form" as="div" class="w-full">
    <CommonAlert v-if="error" :type="alertType" :content="error.message" class="mb-6" />
    <CommonAlert v-if="showVerifyLinkError" type="error" class="mb-6">
      <template #default>
        <i18n-t keypath="pages.account.login.texts.unverified_alert" scope="global">
          <template #link>
            <a href="javascript:void(0)" @click.prevent="requestVerificationLink" class="underline">{{
              $t("pages.account.login.texts.click_here") }}</a>
          </template>
        </i18n-t>
      </template>
    </CommonAlert>
    <form @submit="form.handleSubmit($event, mutate)" class="mx-auto" id="login-form">
      <div class="-mt-2 w-full md:mt-0">
        <div class="flex flex-col md:gap-4 md:pt-4">
          <h1 class="sr-only">
            {{ $t("pages.account.login.title") }}
          </h1>

          <BaseVTextInput type="text" :label="`${$t('common.fields.email')}`" name="email" />

          <BaseVTextInput type="password" :label="`${$t('common.fields.password')}`" name="password" class="md:mt-3" />

          <BaseCheckboxInput name="rememberMe" id="rememberMe" class="mt-1" :size="1.25" :form="form">
            {{ $t("pages.account.login.texts.rememberMe") }}
          </BaseCheckboxInput>
        </div>

        <BaseButton type="submit" :text="`${$t('buttons.common.login')}`" class="mt-4 h-11 w-full text-lg"
          :loading="pending" />

        <NuxtLinkLocale :locale="switchBlogLocale()" to="/account/password-reset"
          class="mb-7 ml-auto mt-5 block w-fit text-sm text-secondary underline hover:no-underline">{{
            $t("pages.account.login.buttons.forgot_password")
          }}</NuxtLinkLocale>
        <div class="mt-4 w-full border-b"></div>
        <BaseTypography variant="p" class="px-2 pt-4 text-center text-sm font-medium md:pb-1">
          {{ $t("pages.account.login.texts.new_to_b2k") }}
          <NuxtLinkLocale :locale="switchBlogLocale()" to="/account/signup"
            class="text-secondary underline hover:no-underline">
            {{ $t("pages.account.login.buttons.create_account") }}
          </NuxtLinkLocale>
        </BaseTypography>
      </div>
    </form>
  </VForm>
</template>

<script setup lang="ts">
import { useProfileStore } from "@/stores/profile";
import * as yup from "yup";
import { useAxios } from "~/composables/utils";
import Application from "~/utils/config";

const { t } = useI18n();

interface PropsType {
  enableRedirect?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
  enableRedirect: true,
});

const error = ref<Error | null>(null);
const showVerifyLinkError = ref(false);
const userId = ref<Number | null>(null);
const verificationToken = ref<String | null>(null);
const alertType = ref<"error" | "success">("error");
const Schema = yup.object({});
type Schema = yup.InferType<typeof Schema>;

interface LoginResponse {
  auth_2fa: boolean;
  auth_token: string;
  data: {
    email: string;
    email_verified: boolean;
    first_name: string;
    last_name: string;
    id: number;
    verification_token: string;
  };
}
const { pending, mutate } = useMutation({
  mutationFn: (credentials: Schema) => useAxios().postForm<LoginResponse>(`/login`, credentials),

  onError() {
    alertType.value = "error";
    error.value = new Error(t("pages.account.login.error_messages.invalid_credentials"));
  },

  async onSuccess({ data }) {
    const profileStore = useProfileStore();
    profileStore.setAuthUser(data.data.id, data.data.verification_token, data.data.email);

    if (data.data.email_verified === false) {
      error.value = null;
      showVerifyLinkError.value = true;
      userId.value = data.data.id;
      verificationToken.value = data.data.verification_token;

    } else {
      const cookie = useCookie(Application.Cookies.authSession, {
        maxAge: 86400,
        path: '/',
        sameSite: 'lax',
      });

      const bus = useNuxtApp().$bus as any;
      bus.emit("user-logged-in");

      // 1. Register new token
      cookie.value = data.auth_token;

      if (!props.enableRedirect) return;

      // 2. Redirect user to account dashboard.
      const localePath = useLocalePath();
      await navigateTo(
        localePath({
          path: "/account",
        }),
      );

    }
  },
});

const requestVerificationLink = async () => {
  try {

    const { data } = await useAxios().get(`/email/verification-notification/${userId.value}/${verificationToken.value}`);

    alertType.value = "success";
    error.value = new Error(t("pages.account.login.success_messages.verification_link_sent"));

  } catch (err: any) {
    alertType.value = "error";
    error.value = new Error(t("pages.account.login.error_messages.failed_verification_link"));
  }
};
</script>
